import { PULSE_FRESHNESS_HOUR_CHOICES, PulseFreshnessHours } from "@swivl-mono/types";
import { SelectOption } from "@swivl-mono/ui";

// The words and choices behind Account settings > Pulse (ENG-3222), apart
// from the tab so they're tested without a browser.

/** The hour a new emailed page goes at, until the member picks one. */
export const DEFAULT_EMAIL_HOUR = 7;
/** The freshness choice that means "my persona's limit". */
export const FRESHNESS_DEFAULT = "default";

/** 0 is "12 AM", 13 is "1 PM". */
export function hourLabel(hour: number): string {
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve} ${hour < 12 ? "AM" : "PM"}`;
}

export const HOUR_OPTIONS: SelectOption[] = Array.from({ length: 24 }, (_, hour) => ({ value: String(hour), label: hourLabel(hour) }));

function hoursLabel(hours: number): string {
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

/** The persona's own limit first, then the member's choices (decision 12). */
export function freshnessOptions(personaHours: number | null): SelectOption[] {
  return [
    { value: FRESHNESS_DEFAULT, label: personaHours ? `Your persona's default (${hoursLabel(personaHours)})` : "Your persona's default" },
    ...PULSE_FRESHNESS_HOUR_CHOICES.map((hours) => ({ value: String(hours), label: hoursLabel(hours) }))
  ];
}

/** A choice from the list; null for the persona's default. */
export function parseFreshness(value: string): PulseFreshnessHours | null {
  return PULSE_FRESHNESS_HOUR_CHOICES.find((hours) => String(hours) === value) ?? null;
}

/** "Eastern Daylight Time (America/New_York)"; the zone's id alone when the browser can't name it. */
export function zoneLabel(zone: string, at: Date = new Date()): string {
  try {
    const name = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "long" })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value;
    return name ? `${name} (${zone})` : zone;
  } catch {
    return zone;
  }
}

/** "Last sent for Thursday, October 1", or "Last sent for the week of September 28". The date is a calendar day, never shifted by a zone. */
export function lastSentLabel(lastEmailedOn: string, cadence: "daily" | "weekly"): string {
  const [year, month, day] = lastEmailedOn.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const format = (options: Intl.DateTimeFormatOptions): string => new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }).format(date);
  return cadence === "weekly"
    ? `Last sent for the week of ${format({ month: "long", day: "numeric" })}`
    : `Last sent for ${format({ weekday: "long", month: "long", day: "numeric" })}`;
}
